import Icon from 'components/common/Icon';
import { ReactSelectValue } from 'types/forms';

import { formatDollars } from 'util/currency';
import { FULL_SERVICE, HYBRID, DIY } from '../SurveyTypePanel/SurveyTypePanel';
import { BillingServiceName, BillingPrice } from 'services/backend/billing';
import { getOptionPrice, getOptionLabel } from '../helpers';
import CircleLoader from 'components/common/CircleLoader';

interface SummaryProps {
  surveyType?: string;
  surveyTotal: number;
  numberOfRespondents: number | undefined;
  estimatedIncidence: ReactSelectValue<string> | null;
  numberOfQuestions: ReactSelectValue<string> | null;
  baseSubtotal: number;
  methodologyComplexity: string;
  methodologyComplexityOptions: ReactSelectValue<string>[] | undefined;
  methodologyFieldworkGroups: string;
  metholologyFieldworkGroupsOptions: ReactSelectValue<string>[] | undefined;
  surveyDetailsData: {
    ordering: number;
    label: string;
    value: BillingServiceName;
    tooltip: string;
    subtitle: string;
    isSelected: boolean;
    onToggle: () => void;
  }[];
  numberOfGroups: string;
  reportingGroupsOptions: ReactSelectValue<string>[] | undefined;
  reportComplexity: string;
  reportComplexityOptions: ReactSelectValue<string>[] | undefined;
  reportingOptionsData: {
    ordering: number;
    label: string;
    value: BillingServiceName;
    tooltip: string;
    subtitle: string;
    isSelected: boolean;
    onToggle: () => void;
  }[];
  reportingSubtotal: number;
  servicesSubtotal: number;
  activePrices: BillingPrice[] | undefined;
  isLoadingQuote: boolean;
}

const summaryHeaderStyling = 'font-medium text-[#99A1AF] mb-2 mt-4';
const summaryDetailStyling = 'text-[#4A5565] my-1';
const summaryTotalsContainerStyling = 'flex justify-between align-center';
const summarySubtotalStyling = 'font-semibold text-[#1E2939] mt-2';

const EmptySummaryDetails = () => (
  <div className="flex flex-col items-center justify-center text-center mb-6">
    <div className="rounded-full h-10 w-10 p-[10px] bg-gray-d-200 my-6">
      <div className="h-5 w-5">
        <Icon id="questionnaire" />
      </div>
    </div>
    <p>Your quote summary will apear here as you make selections.</p>
  </div>
);

const Summary = ({
  surveyType,
  surveyTotal,
  numberOfRespondents,
  estimatedIncidence,
  numberOfQuestions,
  baseSubtotal,
  methodologyComplexity,
  methodologyComplexityOptions,
  methodologyFieldworkGroups,
  metholologyFieldworkGroupsOptions,
  surveyDetailsData,
  numberOfGroups,
  reportingGroupsOptions,
  reportComplexity,
  reportComplexityOptions,
  reportingOptionsData,
  reportingSubtotal,
  servicesSubtotal,
  activePrices,
  isLoadingQuote,
}: SummaryProps) => {
  return (
    <div className="border-1 border-gray-d-300 bg-white rounded-lg w-[275px] shrink-0 h-fit self-start sticky top-0 text-xs">
      <div className="bg-primary-d-700 rounded-t-lg p-4 text-[#8BB5B1]">
        <p className="mb-1">ESTIMATED QUOTE</p>
        {surveyTotal || isLoadingQuote ? (
          <p>
            {isLoadingQuote ? (
              <p className="flex items-center min-h-9">
                <CircleLoader />
              </p>
            ) : (
              <>
                <span className="text-3xl text-white font-bold">
                  {formatDollars(surveyTotal)}
                </span>{' '}
                <span>est.</span>
              </>
            )}
          </p>
        ) : (
          <div>
            <p className="text-xl">-</p>
            <p>Fill in your details to see an estimate</p>
          </div>
        )}
      </div>
      <div className="p-4 text-gray-500">
        {surveyType ? (
          <>
            <div>
              <p className="font-medium text-[#99A1AF] mb-2">SURVEY TYPE</p>
              <p className={summaryDetailStyling}>{surveyType} Survey</p>
            </div>
            {(numberOfRespondents ||
              estimatedIncidence ||
              numberOfQuestions) && (
              <div>
                <p className={summaryHeaderStyling}>FIELDING</p>
                {numberOfRespondents && (
                  <p className={summaryDetailStyling}>
                    {numberOfRespondents} respondents
                  </p>
                )}
                {estimatedIncidence && (
                  <p className={summaryDetailStyling}>
                    {estimatedIncidence.label} incidence
                  </p>
                )}
                {numberOfQuestions && (
                  <p className={summaryDetailStyling}>
                    {numberOfQuestions.label} questions
                  </p>
                )}
                <div className={summaryTotalsContainerStyling}>
                  <p className={summarySubtotalStyling}>Base fielding cost</p>
                  <p className={summarySubtotalStyling}>
                    {formatDollars(baseSubtotal)}
                  </p>
                </div>
              </div>
            )}
            {surveyType === FULL_SERVICE &&
              (numberOfRespondents ||
                estimatedIncidence ||
                numberOfQuestions) && (
                <div>
                  <p className={summaryHeaderStyling}>
                    DESIGN & FIELDING SERVICES
                  </p>
                  <p className={summaryDetailStyling}>All services included</p>
                </div>
              )}
            {surveyType === HYBRID &&
              surveyDetailsData.some((detail) => detail.isSelected) && (
                <div>
                  <p className={summaryHeaderStyling}>
                    DESIGN & FIELDING SERVICES
                  </p>
                  {surveyDetailsData
                    .filter((detail) => detail.isSelected)
                    .map((detail, i) => (
                      <div
                        key={`survey-details-summary-${i}`}
                        className={summaryTotalsContainerStyling}
                      >
                        <p className={summaryDetailStyling}>{detail.label}</p>
                        <p className={summaryDetailStyling}>
                          {getOptionPrice(detail.value, activePrices)}
                        </p>
                      </div>
                    ))}
                  <div className={summaryTotalsContainerStyling}>
                    <p className={summarySubtotalStyling}>Services subtotal</p>
                    <p className={summarySubtotalStyling}>
                      {formatDollars(servicesSubtotal)}
                    </p>
                  </div>
                </div>
              )}
            {surveyType !== DIY &&
              (methodologyComplexity || methodologyFieldworkGroups) && (
                <div>
                  <p className={summaryHeaderStyling}>
                    METHODOLOGY & FIELDWORK
                  </p>
                  {methodologyComplexity && (
                    <div className={summaryTotalsContainerStyling}>
                      <p className={summaryDetailStyling}>
                        Methodology Complexity
                      </p>
                      <p className={summaryDetailStyling}>
                        {getOptionLabel(
                          methodologyComplexity,
                          methodologyComplexityOptions,
                        )}
                      </p>
                    </div>
                  )}
                  {methodologyFieldworkGroups && (
                    <div className={summaryTotalsContainerStyling}>
                      <p className={summaryDetailStyling}>
                        Fieldwork Sample Groups
                      </p>
                      <p className={summaryDetailStyling}>
                        {getOptionLabel(
                          methodologyFieldworkGroups,
                          metholologyFieldworkGroupsOptions,
                        )}
                      </p>
                    </div>
                  )}
                </div>
              )}
            {reportingOptionsData.some((option) => option.isSelected) && (
              <div>
                <p className={summaryHeaderStyling}>REPORTING & ANALYSIS</p>
                {reportingOptionsData
                  .filter((option) => option.isSelected)
                  .map((option, i) => (
                    <div
                      key={`reporting-options-summary-${i}`}
                      className={summaryTotalsContainerStyling}
                    >
                      <p className={summaryDetailStyling}>{option.label}</p>
                      <p className={summaryDetailStyling}>
                        {getOptionPrice(option.value, activePrices)}
                      </p>
                    </div>
                  ))}
                <div className={summaryTotalsContainerStyling}>
                  <p className={summarySubtotalStyling}>Reporting subtotal</p>
                  <p className={summarySubtotalStyling}>
                    {formatDollars(reportingSubtotal)}
                  </p>
                </div>
              </div>
            )}
            {surveyType !== DIY && (numberOfGroups || reportComplexity) && (
              <div>
                <p className={summaryHeaderStyling}>REPORTING COMPLEXITY</p>
                {reportComplexity && (
                  <div className={summaryTotalsContainerStyling}>
                    <p className={summaryDetailStyling}>Reporting Complexity</p>
                    <p className={summaryDetailStyling}>
                      {getOptionLabel(
                        reportComplexity,
                        reportComplexityOptions,
                      )}
                    </p>
                  </div>
                )}
                {numberOfGroups && (
                  <div className={summaryTotalsContainerStyling}>
                    <p className={summaryDetailStyling}>Groups/Subgroups</p>
                    <p className={summaryDetailStyling}>
                      {getOptionLabel(numberOfGroups, reportingGroupsOptions)}
                    </p>
                  </div>
                )}
              </div>
            )}
            {(surveyTotal || isLoadingQuote) && (
              <>
                <div>
                  <hr className="mt-4 border-[#F3F4F6]" />
                  <div className={summaryTotalsContainerStyling}>
                    <p className="font-semibold text-[#1E2939] my-4 text-lg">
                      Total Estimate:
                    </p>
                    {isLoadingQuote ? (
                      <p className="flex items-center my-4">
                        <CircleLoader isColored />
                      </p>
                    ) : (
                      <p className="font-semibold text-[#1E2939] my-4 text-lg">
                        {formatDollars(surveyTotal)}
                      </p>
                    )}
                  </div>
                </div>
                <p className="text-center">
                  Pricing is an estimate. A Research Director will confirm final
                  costs.
                </p>
              </>
            )}
          </>
        ) : (
          <EmptySummaryDetails />
        )}
      </div>
    </div>
  );
};

export default Summary;
